import React, { useEffect, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import clsx from "clsx";
import PropTypes from "prop-types";

import usePrevious from "@/js/utils/usePrevious";
import { useScreenshareStore } from "@/stores/v2/screenshareStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import Api from "@/js/api/authorisedApi";
import iconHelpBlue from "@/assets/IconHelpBlue.svg?url";
import FastWindowScreenItem from "@/js/components/RegularClassroomUser/components/FastWindowScreenItem";
import { isBeingRestricted } from "@/js/components/RegularClassroomUser/utils";
import FastWindowTabItem from "@/js/components/RegularClassroomUser/components/FastWindowTabItem";
import WebsiteOrSignatureListModal from "@/js/components/WebsiteOrSignatureListModal";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const StudentTileContent = (props) => {
	const [tabsViewShowHiddenTabs, setTabViewShowHiddenTabs] = useState(false);
	const [isScreenshotLoading, setIsScreenshotLoading] = useState(false);

	const prevView = usePrevious(props.view);
	const prevWindowData = usePrevious(props.windowData);

	const closedTabId = useRef();

	// states to handle tab list view modal
	const [listModalTitle, setListModalTitle] = useState("");
	const [listModalItems, setListModalItems] = useState([]);
	const [listModalIsMonitorMode, setListModalIsMonitorMode] = useState(false);
	const [listModalOpen, setListModalOpen] = useState(false);

	const screenshareState = useScreenshareStore();

	// didUpdate
	useEffect(() => {
		if (prevView !== props.view) {
			setTabViewShowHiddenTabs(false);
		}

		if (prevWindowData !== props.windowData && isScreenshotLoading) {
			if (!closedTabId.current || closedTabId.current !== props.windowData.tab_id) {
				setIsScreenshotLoading(false);
			}
		}
	}, [props.windowData, props.view, isScreenshotLoading, prevView, prevWindowData]);

	const isClassActive = () => props.isClassActive;
	const isChromeActive = () => props.windowData && Object.keys(props.windowData).length;
	const isUserPaused = useClassroomPolicyStore.getState().getPausedStudents().includes(props.user.username);

	const forceCloseTab = (tabId, setLoading = false) => {
		if (!props.canCloseTab || !tabId) return;

		const split = tabId.split("_");

		Api.post(
			"/event/close-tab",
			{
				recipients: [props.user.username],
				event_object: {
					windowId: split[0],
					tabId: split[1],
				},
			},
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			(result) => {
				if (setLoading) {
					if (props.windowData.background_tabs && props.windowData.background_tabs.length) {
						closedTabId.current = tabId;
						setIsScreenshotLoading(true);
					}
				}
			}
		);
	};

	const openZenDeskWaitingForStudentActivity = () => {
		window.open("https://help.classwize.net/hc/en-gb/articles/6227484666652-Waiting-for-Student-Activity", "_blank");
	};

	// tab list view modal handler
	const openListModal = (title, items, isMonitorMode) => {
		setListModalTitle(title);
		setListModalItems(items);
		setListModalIsMonitorMode(isMonitorMode);
		setListModalOpen(true);
	};
	const closeListModal = () => {
		setListModalOpen(false);
	};

	const renderContentView = () => {
		if (!isClassActive()) {
			return (
				<div className="StudentTileContent_offline">
					<p>Start class to see student activity...</p>
					<button href="#" onClick={openZenDeskWaitingForStudentActivity} target="_blank" rel="noopener noreferrer">
						<img className="ClassroomSession_tipIcon" src={iconHelpBlue} alt="" />
						Find out more
					</button>
				</div>
			);
		}

		if (!isChromeActive()) {
			return (
				<div className="StudentTileContent_offline">
					<p>Waiting for student activity...</p>
					<button href="#" onClick={openZenDeskWaitingForStudentActivity} target="_blank" rel="noopener noreferrer">
						<img className="ClassroomSession_tipIcon" src={iconHelpBlue} alt="" />
						Find out more
					</button>
				</div>
			);
		}

		if (isChromeActive()) {
			if (props.view === "screens" && Object.keys(props.windowData).length) {
				return (
					<div className="StudentTileContent_screenContent">
						<FastWindowScreenItem
							user={props.user}
							windowData={props.windowData}
							signatureDb={props.signatureDb}
							forceCloseTab={forceCloseTab}
							canCloseTab={props.canCloseTab}
							isScreenshotLoading={isScreenshotLoading}
							classIsActive={props.isClassActive}
							isMonitorMode={props.classroom.session?.isMonitoring}
							openP2pModal={props.openP2pModal}
							tabItemLimit={props.tabItemLimit}
							isRestricted={isBeingRestricted(props.windowData)}
							inScreenshare={screenshareState.screenshareStudents.has(props.user.username)}
						/>
					</div>
				);
			}
			if (props.view === "tabs") {
				const backgroundTabs = props.windowData.background_tabs
					? props.windowData.background_tabs.map((bt) => ({
							url: bt.tabUrl,
							favicon: bt.favIcon,
							title: bt.title,
							tab_id: bt.tab_id,
						}))
					: [];
				const tabsData = [props.windowData, ...backgroundTabs];
				const tabsToDisplay = tabsViewShowHiddenTabs ? tabsData : tabsData.slice(0, props.tabItemLimit);
				return (
					<div className="StudentTileContent_tabsList">
						{tabsToDisplay.map((tab, i) => (
							<FastWindowTabItem
								key={`${tab.tab_id || tab.url}-${i}`}
								item={tab}
								chromeId={props.windowData.chrome_id}
								chromeWindowId={props.windowData.chrome_window_id}
								signatureDb={props.signatureDb}
								user={props.user}
								forceCloseTab={forceCloseTab}
								canCloseTab={props.canCloseTab}
								classIsActive={props.isClassActive}
								openP2pModal={props.openP2pModal}
								isMonitorMode={props.classroom.session?.isMonitoring}
								isRestricted={isBeingRestricted(props.windowData)}
							/>
						))}
						<WebsiteOrSignatureListModal
							title={listModalTitle}
							open={listModalOpen}
							activeTab={tabsToDisplay[0]}
							items={listModalItems}
							signatureDb={props.signatureDb}
							user={props.user}
							onClose={closeListModal}
							forceCloseTab={forceCloseTab}
							canCloseTab={props.canCloseTab}
							classIsActive={props.isClassActive}
							isMonitorMode={listModalIsMonitorMode}
						/>
						{tabsData.length > props.tabItemLimit ? (
							<div className="StudentTileContent_showMoreTabs">
								<button className="" onClick={() => openListModal("Open Tabs", props.windowData.background_tabs, listModalIsMonitorMode)}>
									{tabsViewShowHiddenTabs ? "Hide" : `Show All (${tabsData.length})`}
								</button>
							</div>
						) : null}
					</div>
				);
			}
		}
	};

	return (
		<div
			className={clsx("StudentTileContent", {
				"StudentTileContent--expanded": tabsViewShowHiddenTabs,
			})}
		>
			{isUserPaused && (
				<Box
					background={"#FFDBDF"}
					rounded={"md"}
					textAlign={"center"}
					py={0.5}
					fontSize={"sm"}
					mb={2}
					fontWeight={500}
					position={"absolute"}
					top={"16px"}
					left={"50%"}
					transform={"translate(-50%, -50%)"}
					width={"94%"}
				>
					Internet Paused
				</Box>
			)}
			{renderContentView()}
		</div>
	);
};

StudentTileContent.propTypes = {
	user: PropTypes.object.isRequired,
	windowData: PropTypes.object.isRequired,
	signatureDb: PropTypes.object.isRequired,
	view: PropTypes.oneOf(["screens", "tabs"]),
	canCloseTab: PropTypes.bool,
	isClassActive: PropTypes.bool,
	classroom: PropTypes.object,
	openP2pModal: PropTypes.func,
	tabItemLimit: PropTypes.number,
};
